import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

dayjs.extend(utc);

export function convertISOToDashedFullDateString(isoDateString: string) {
  if (typeof isoDateString !== 'string') {
    return 'Invalid Date';
  }

  const date = new Date(isoDateString);
  if (Number.isNaN(date.getTime())) {
    return '';
  }

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');

  return `${year}-${month}-${day}`;
}

export function convertDateToParamsFormat(date: Date) {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) {
    return '';
  }

  const year = date.getFullYear();
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const day = date.getDate().toString().padStart(2, '0');

  return `${year}-${month}-${day}`;
}

export function convertISOToDottedDateTimeString(isoDateString: string) {
  if (typeof isoDateString !== 'string') {
    return 'Invalid Date';
  }

  const date = new Date(isoDateString);
  if (Number.isNaN(date.getTime())) {
    return '';
  }

  const year = date.getFullYear().toString().slice(-2);
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const day = date.getDate().toString().padStart(2, '0');
  const hours = date.getHours().toString().padStart(2, '0');
  const minutes = date.getMinutes().toString().padStart(2, '0');
  const seconds = date.getSeconds().toString().padStart(2, '0');

  return `${year}.${month}.${day} ${hours}:${minutes}:${seconds}`;
}

export function convertISOToDottedDateTimeStringNotSeconds(isoDateString: string) {
  if (typeof isoDateString !== 'string') {
    return 'Invalid Date';
  }

  const date = new Date(isoDateString);
  if (Number.isNaN(date.getTime())) {
    return '';
  }

  const year = date.getFullYear().toString().slice(-2);
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const day = date.getDate().toString().padStart(2, '0');
  const hours = date.getHours().toString().padStart(2, '0');
  const minutes = date.getMinutes().toString().padStart(2, '0');

  return `${year}.${month}.${day} ${hours}:${minutes}`;
}

export function convertISOToDottedSimpleDateTimeString(isoDateString: string) {
  if (typeof isoDateString !== 'string') {
    return 'Invalid Date';
  }

  const date = new Date(isoDateString);
  if (Number.isNaN(date.getTime())) {
    return '';
  }

  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const day = date.getDate().toString().padStart(2, '0');
  const hours = date.getHours().toString().padStart(2, '0');
  const minutes = date.getMinutes().toString().padStart(2, '0');

  return `${month}.${day} ${hours}:${minutes}`;
}

export function convertISOToDottedFullDateString(isoDateString: string, delimiter = '.') {
  if (typeof isoDateString !== 'string') {
    return 'Invalid Date';
  }

  const date = new Date(isoDateString);
  if (Number.isNaN(date.getTime())) {
    return '';
  }

  const year = date.getFullYear().toString();
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const day = date.getDate().toString().padStart(2, '0');

  return `${year}${delimiter}${month}${delimiter}${day}`;
}

export function convertISOToDottedShortDateString(isoDateString: string) {
  if (typeof isoDateString !== 'string') {
    return '';
  }

  const date = new Date(isoDateString);
  if (Number.isNaN(date.getTime())) {
    return '';
  }

  const year = date.getFullYear().toString().slice(-2);
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const day = date.getDate().toString().padStart(2, '0');

  return `${year}.${month}.${day}`;
}

export function convertISOToDottedYYMM(isoDateString: string) {
  if (typeof isoDateString !== 'string') {
    return 'Invalid Date';
  }

  const date = new Date(isoDateString);
  if (Number.isNaN(date.getTime())) {
    return '';
  }

  const year = date.getFullYear().toString().slice(-2);
  const month = (date.getMonth() + 1).toString().padStart(2, '0');

  return `${year}.${month}`;
}

export function createTimeArray(start: string, end: string, increment: number): string[] {
  const result: string[] = [];
  let currentMoment = new Date(`1970-01-01T${start}Z`);
  const endMoment = new Date(`1970-01-01T${end}Z`);

  while (currentMoment <= endMoment) {
    const hours = String(currentMoment.getUTCHours()).padStart(2, '0');
    const minutes = String(currentMoment.getUTCMinutes()).padStart(2, '0');
    result.push(`${hours}:${minutes}`);
    currentMoment = new Date(currentMoment.getTime() + increment * 60000);
  }

  return result;
}

/**
 * @param startTimeString - ('2024-10-24 12:00:40');
 * @returns  'HH:MM'
 */
export function formatTimeElapsedSince(startTimeString: string): string {
  const startTime = dayjs(startTimeString);
  if (!startTime.isValid()) {
    throw new Error('Invalid input: startTimeString must be a valid date string.');
  }

  const currentTime = dayjs();
  const timeDiff = currentTime.diff(startTime);
  const hours = Math.floor(timeDiff / (1000 * 60 * 60));
  const minutes = Math.floor((timeDiff / (1000 * 60)) % 60);

  const formattedHours = hours < 10 ? `0${hours}` : `${hours}`;
  const formattedMinutes = minutes < 10 ? `0${minutes}` : `${minutes}`;

  return `${formattedHours}:${formattedMinutes}`;
}

export function formatDateToISOWithTimezone(date: Date): string {
  const seoulTimeZoneOffset = 9; // KST (UTC+9)
  const isoString = new Date(date.getTime() + seoulTimeZoneOffset * 60 * 60 * 1000).toISOString();
  const isoDate = isoString.slice(0, 19);
  const offset = '+';
  const hoursOffset = String(Math.floor(seoulTimeZoneOffset)).padStart(2, '0');
  const minutesOffset = String((seoulTimeZoneOffset % 1) * 60).padStart(2, '0');

  return `${isoDate}${offset}${hoursOffset}:${minutesOffset}`;
}
